import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiCalendar } from 'react-icons/fi';
import Sidebar from './Sidebar';
import ParticlesBackground from './ParticlesBackground';
import FloatingActionButton from './FloatingActionButton';
import { useFinancialYear } from '../../context/FinancialYearContext';

function formatFYDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function FinancialYearBadge({ compact }) {
  const { activeFY } = useFinancialYear();
  const navigate = useNavigate();

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: compact ? '6px 12px' : '7px 16px',
    borderRadius: 999,
    border: '1.5px solid rgba(16, 185, 129, 0.35)',
    background: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    boxShadow: '0 2px 10px rgba(6, 78, 59, 0.08)',
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
    whiteSpace: 'nowrap',
  };

  if (!activeFY) {
    return (
      <button
        onClick={() => navigate('/settings')}
        title="No active financial year — click to set one in Settings"
        style={{
          ...baseStyle,
          border: '1.5px solid rgba(245, 158, 11, 0.45)',
          background: 'rgba(254, 243, 199, 0.8)',
        }}
      >
        <FiCalendar size={14} color="#b45309" />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}>
          Set Financial Year
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={() => navigate('/settings')}
      title={`Active Financial Year: ${formatFYDate(activeFY.start_date)} — ${formatFYDate(activeFY.end_date)}`}
      style={baseStyle}
    >
      <FiCalendar size={14} color="#059669" />
      <span style={{ fontSize: compact ? 12 : 13, fontWeight: 700, color: '#064e3b' }}>
        FY {activeFY.label}
      </span>
      {!compact && (
        <span style={{ fontSize: 11, fontWeight: 500, color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
          {formatFYDate(activeFY.start_date)} — {formatFYDate(activeFY.end_date)}
        </span>
      )}
    </button>
  );
}

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sidebarWidth = isMobile ? 0 : (collapsed ? 72 : 260);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', width: '100vw', position: 'relative' }}>
      <ParticlesBackground />

      {/* Mobile backdrop overlay */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 150,
          }}
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          flex: 1,
          marginLeft: sidebarWidth,
          width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
          maxWidth: '100%',
          padding: isMobile ? '16px' : '24px 32px',
          minHeight: '100vh',
          position: 'relative',
          overflowX: 'hidden',
          overflowY: 'auto',
          boxSizing: 'border-box',
          transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out, padding 0.3s ease',
        }}
      >
        {/* Mobile top bar with hamburger */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
          }}>
            <button
              onClick={() => setMobileOpen(true)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #059669, #10b981)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                flexShrink: 0,
              }}
              aria-label="Open menu"
            >
              <FiMenu size={20} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img
                src="/ems-logo-ico.png"
                alt="EMS"
                style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6 }}
              />
              <span style={{ fontWeight: 700, fontSize: 14, color: '#064e3b', fontFamily: 'Poppins, sans-serif' }}>
                Export Software
              </span>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <FinancialYearBadge compact />
            </div>
          </div>
        )}
        {!isMobile && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <FinancialYearBadge />
          </div>
        )}
        <Outlet />
      </motion.main>

      <FloatingActionButton />
    </div>
  );
}
